import { readableColor, rgba, transparentize } from 'polished';
import styled, { css, keyframes } from 'styled-components';
import { safePolished } from '@/utils/safePolished';
import { COLORS, LAYOUT, ORDER } from '@/constants/styles';
import { getFontStyles } from '@/redux/cmsConfig/utils';

export const StyledDropdownOverlay = styled.div<{ $isVisible: boolean }>`
  background-color: ${COLORS.SEMI_TRANSPARENT};
  left: 0;
  height: 100%;
  position: fixed;
  top: 0;
  width: 100%;
  z-index: ${ORDER.ACCOUNT_DROPDOWN};
  visibility: ${(props) => (props.$isVisible ? 'visible' : 'hidden')};
  opacity: ${(props) => (props.$isVisible ? 1 : 0)};
  transition: all 0.2s linear;

  @media (max-width: 767px) {
    top: 48px;
    height: calc(100% - 48px);
  }
`;

interface DropdownModalContainer {
  $isVisible: boolean;
}

export const StyledDropdownModalContainer = styled.nav<DropdownModalContainer>(
  ({ theme, $isVisible }) => ({
    background: theme.modals.background_color ?? 'white',
    overflowY: 'scroll',
    maxHeight: '100%',
    width: '320px',
    position: 'fixed',
    top: '0px',
    right: '20px',
    transform: `translateY(${$isVisible ? 0 : -150}px)`,
    transition: 'all 0.2s linear',
    ...getFontStyles(theme.modals.font),

    '@media (max-width: 767px)': {
      top: '48px',
      left: 0,
      right: 0,
      width: '100%',
      maxHeight: 'calc(100% - 48px)',
      borderRadius: '0 0 12px 12px',
    },
  }),
);

export const StyledAccountDropdownContent = styled.div`
  display: flex;
  flex-direction: column;
  text-align: left;
  padding-bottom: 16px;

  ul {
    padding: 0;
    margin: 0;
    list-style: none;
  }
`;

export const StyledDropdownHeaderContent = styled.div`
  padding-top: ${LAYOUT.GUTTER}px;
  padding-bottom: ${LAYOUT.GUTTER}px;

  h3 {
    padding: 0px ${LAYOUT.GUTTER}px;
    font-size: 20px;
    font-family: ${({ theme }) => theme.primary_font.bold.font_family};
    margin: 0;
  }

  p {
    margin: ${LAYOUT.GUTTER / 4}px 0 0;
  }
`;

export const StyledLoyaltyAccessorContainer = styled.div`
  padding: 0px 8px;
`;

/** Shared row base: 48px, icon + label, hover/pressed/focus states. */
const rowBaseCss = css`
  ${({ theme }) => {
    const fontColor = theme.modals.active_font_color ?? '#061b46';
    return `
      color: ${fontColor};
      &:hover {
        background: ${rgba(fontColor, 0.04)};
      }
      &:active {
        background: ${rgba(fontColor, 0.1)};
      }
      &:focus-visible {
        outline: 2px solid ${fontColor};
        outline-offset: -2px;
        border-radius: 8px;
      }
    `;
  }}

  width: 100%;
  height: 48px;
  font-size: 16px;
  cursor: pointer;
  background: none;
  border: none;
  font-family: ${({ theme }) => theme.primary_font.bold.font_family};
  text-align: left;
  padding: 0 ${LAYOUT.GUTTER}px;
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
`;

export const StyledDropdownButton = styled.a`
  ${rowBaseCss}
`;

export const StyledSignOutButton = styled.a`
  ${rowBaseCss}
  height: 44px;

  @media (max-width: 767px) {
    height: 46px;
  }
`;

export const StyledRowDivider = styled.li`
  height: 1px;
  background: ${({ theme }) => theme.modals.border_color ?? '#e0e0e0'};
  margin: 4px ${LAYOUT.GUTTER}px;
  opacity: 0.4;
`;

export const StyledIconWrapper = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  color: ${({ theme }) => theme.modals.active_font_color ?? '#061b46'};
`;

export const StyledRowLabel = styled.span`
  font-size: 15px;
  flex: 1;
  font-weight: 500;
`;

export const StyledRowLabelSignOut = styled.span`
  font-size: 14px;
  flex: 1;
  font-weight: 400;
`;

export const StyledAccountDropdownNumber = styled.span`
  ${({ theme }) => {
    const fontColor = theme.modals.active_font_color ?? '#061b46';
    return `
      background: ${fontColor};
      color: ${safePolished(readableColor)(fontColor)};
    `;
  }}
  border-radius: 50%;
  font-size: 12px;
  height: 22px;
  min-width: 22px;
  padding: 0 4px;
  text-align: center;
  line-height: 22px;
  font-family: ${({ theme }) => theme.modals.font?.font_family ?? 'inherit'};
`;

// Loyalty Points Summary styles

export const StyledLoyaltySummary = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: ${({ theme }) => theme.modals.border_radius ?? 8}px;
  background: ${({ theme }) =>
    safePolished(transparentize)(0.95, theme.global.primary_active_color) || '#fef5f4'};
  margin-top: ${LAYOUT.GUTTER / 2}px;
`;

export const StyledLoyaltyPointsContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

export const StyledPointsText = styled.p`
  font-size: 18px;
  font-weight: 700;
  line-height: 22px;
  font-family: ${({ theme }) => theme.primary_font.bold.font_family};
  color: ${({ theme }) => theme.modals.active_font_color ?? '#061b46'};
  margin: 0;
`;

export const StyledPointsSubtext = styled.p`
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: ${({ theme }) => theme.modals.font?.color ?? '#374151'};
  margin: 0;
`;

export const StyledProgressBar = styled.div`
  height: 6px;
  border-radius: 999px;
  overflow: hidden;
  margin-top: 6px;
  width: 100%;
  background: ${({ theme }) =>
    safePolished(transparentize)(0.85, theme.global.primary_active_color) || '#f2d6d3'};
`;

export const StyledProgressBarFill = styled.div<{ $width: string }>`
  height: 100%;
  border-radius: 999px;
  background: ${({ theme }) => theme.global.primary_active_color ?? '#d60800'};
  width: ${({ $width }) => $width};
  transition: width 0.3s ease;
`;

const pulseAnimation = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

export const StyledLoyaltySkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border-radius: ${({ theme }) => theme.modals.border_radius ?? 8}px;
  background: ${({ theme }) =>
    safePolished(transparentize)(0.95, theme.global.primary_active_color) || '#fef5f4'};
  margin-top: ${LAYOUT.GUTTER / 2}px;
  height: 68px;
  animation: ${pulseAnimation} 1.5s ease-in-out infinite;
`;

export const StyledRewardsBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 16px;
  flex-shrink: 0;
  background: ${({ theme }) => theme.modals.active_font_color ?? '#061b46'};
  color: ${({ theme }) => safePolished(readableColor)(theme.modals.active_font_color) || 'white'};
  font-size: 14px;
`;
